import { useCallback, useState } from 'react';

import { TASKS_GRID_VIEW_OPTIONS_STORAGE_KEY } from 'constants/uiConstants';

/** The category sheet's optional value columns. Input name and Rate always show. */
// Types, not interfaces, so each reads as a Record<string, boolean> in the
// View options' generic checkbox lists.
export type SheetColumnVisibility = {
  rateUnit: boolean;
  required: boolean;
  costPerUnit: boolean;
  costPerHa: boolean;
};

/** What the Task cell shows besides the task type, which always shows. */
export type SheetTaskCellVisibility = {
  area: boolean;
  date: boolean;
  timing: boolean;
  waterRate: boolean;
  target: boolean;
};

export type TaskColumnWidth = 'auto' | 'full' | 'trimmed';
const TASK_COLUMN_WIDTHS: TaskColumnWidth[] = ['auto', 'full', 'trimmed'];

export type SheetRowVisibility = {
  notes: boolean;
  totals: boolean;
};

/**
 * The Tasks grid's view options (OP-2556). `sheetLayout` switches the new
 * layout on; everything else shapes it. Remembered in this browser.
 */
export interface TasksViewPreferences {
  sheetLayout: boolean;
  /**
   * The Task column's width: `auto` trims it when the sheet is narrow;
   * `full` and `trimmed` are the user's own choice from its « / » button,
   * which then holds at any width.
   */
  taskColumnWidth: TaskColumnWidth;
  columns: SheetColumnVisibility;
  taskCell: SheetTaskCellVisibility;
  rows: SheetRowVisibility;
}

export const DEFAULT_TASKS_VIEW_PREFERENCES: TasksViewPreferences = {
  sheetLayout: false,
  taskColumnWidth: 'auto',
  columns: {
    rateUnit: true,
    required: true,
    costPerUnit: true,
    costPerHa: true,
  },
  taskCell: {
    area: true,
    date: true,
    timing: true,
    waterRate: true,
    target: true,
  },
  rows: { notes: true, totals: true },
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// Keeps only known boolean keys, so a stored object from an older or newer
// version can't add or break anything; anything missing takes its default.
const mergeBooleans = <T extends object>(defaults: T, stored: unknown): T => {
  if (!isRecord(stored)) return defaults;
  const merged = { ...defaults };
  (Object.keys(defaults) as (keyof T)[]).forEach((key) => {
    const value = stored[key as string];
    if (typeof value === 'boolean') {
      (merged as Record<keyof T, unknown>)[key] = value;
    }
  });
  return merged;
};

const loadPreferences = (): TasksViewPreferences => {
  try {
    const raw = localStorage.getItem(TASKS_GRID_VIEW_OPTIONS_STORAGE_KEY);
    if (!raw) return DEFAULT_TASKS_VIEW_PREFERENCES;
    const stored: unknown = JSON.parse(raw);
    if (!isRecord(stored)) return DEFAULT_TASKS_VIEW_PREFERENCES;
    const defaults = DEFAULT_TASKS_VIEW_PREFERENCES;
    return {
      sheetLayout:
        typeof stored.sheetLayout === 'boolean'
          ? stored.sheetLayout
          : defaults.sheetLayout,
      taskColumnWidth: TASK_COLUMN_WIDTHS.includes(
        stored.taskColumnWidth as TaskColumnWidth
      )
        ? (stored.taskColumnWidth as TaskColumnWidth)
        : defaults.taskColumnWidth,
      columns: mergeBooleans(defaults.columns, stored.columns),
      taskCell: mergeBooleans(defaults.taskCell, stored.taskCell),
      rows: mergeBooleans(defaults.rows, stored.rows),
    };
  } catch {
    // Storage unavailable or unreadable — the defaults.
    return DEFAULT_TASKS_VIEW_PREFERENCES;
  }
};

/**
 * The Tasks grid's view options, remembered in this browser (OP-2556).
 * `resetViewOptions` restores every option except the layout itself.
 */
export const useTasksViewPreferences = () => {
  const [preferences, setPreferences] = useState(loadPreferences);

  const updatePreferences = useCallback(
    (update: (previous: TasksViewPreferences) => TasksViewPreferences) => {
      setPreferences((previous) => {
        const next = update(previous);
        try {
          localStorage.setItem(
            TASKS_GRID_VIEW_OPTIONS_STORAGE_KEY,
            JSON.stringify(next)
          );
        } catch {
          // Storage unavailable — the choice still applies for this visit.
        }
        return next;
      });
    },
    []
  );

  const resetViewOptions = useCallback(
    () =>
      updatePreferences((previous) => ({
        ...DEFAULT_TASKS_VIEW_PREFERENCES,
        sheetLayout: previous.sheetLayout,
      })),
    [updatePreferences]
  );

  return { preferences, updatePreferences, resetViewOptions };
};
